import React, { useState, useEffect } from 'react';
import { X, Save, Plus, Trash2 } from 'lucide-react';

const TemplateForm = ({ template, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    nom: '',
    message: '',
    type_notification: 'INFO',
    priorite: 'NORMALE',
    variables: [],
    est_actif: true
  });
  const [newVariable, setNewVariable] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (template) {
      setFormData({
        nom: template.nom || '',
        message: template.message || '',
        type_notification: template.type_notification || 'INFO',
        priorite: template.priorite || 'NORMALE',
        variables: template.variables || [],
        est_actif: template.est_actif !== undefined ? template.est_actif : true
      });
    }
  }, [template]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    } finally {
      setLoading(false);
    }
  };

  const addVariable = () => {
    if (newVariable && !formData.variables.includes(newVariable)) {
      setFormData({
        ...formData,
        variables: [...formData.variables, newVariable]
      });
      setNewVariable('');
    }
  };

  const removeVariable = (variable) => {
    setFormData({
      ...formData,
      variables: formData.variables.filter(v => v !== variable)
    });
  };

  const insertVariable = (variable) => {
    const textarea = document.getElementById('message-textarea');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = formData.message;
    const before = text.substring(0, start);
    const after = text.substring(end);
    
    setFormData({
      ...formData,
      message: before + `{${variable}}` + after
    });

    // Remettre le focus après l'insertion
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + variable.length + 2;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col my-8">
        {/* En-tête */}
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white z-10">
          <h2 className="text-2xl font-bold text-gray-900">
            {template ? 'Modifier le modèle' : 'Nouveau modèle'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Nom du modèle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom du modèle <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.nom}
              onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
              placeholder="Ex: Bienvenue nouveau membre"
            />
          </div>

          {/* Type et Priorité */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type de notification
              </label>
              <select
                value={formData.type_notification}
                onChange={(e) => setFormData({ ...formData, type_notification: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
              >
                <option value="INFO">📢 Information</option>
                <option value="AVERTISSEMENT">⚠️ Avertissement</option>
                <option value="URGENCE">🚨 Urgence</option>
                <option value="FELICITATION">🎉 Félicitation</option>
                <option value="RAPPEL">⏰ Rappel</option>
                <option value="EVENEMENT">📅 Événement</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priorité par défaut
              </label>
              <select
                value={formData.priorite}
                onChange={(e) => setFormData({ ...formData, priorite: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
              >
                <option value="BASSE">Basse</option>
                <option value="NORMALE">Normale</option>
                <option value="HAUTE">Haute</option>
                <option value="CRITIQUE">Critique</option>
              </select>
            </div>
          </div>

          {/* Variables */}
          <div className="border border-gray-300 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Variables personnalisables
            </label>
            
            {/* Liste des variables */}
            {formData.variables.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {formData.variables.map((variable, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-800 rounded-lg group"
                  >
                    <span
                      className="font-mono text-sm cursor-pointer hover:underline"
                      onClick={() => insertVariable(variable)}
                      title="Cliquer pour insérer"
                    >
                      {`{${variable}}`}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeVariable(variable)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Ajouter une variable */}
            <div className="flex gap-2">
              <input
                type="text"
                value={newVariable}
                onChange={(e) => setNewVariable(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addVariable())}
                placeholder="nom_variable"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <button
                type="button"
                onClick={addVariable}
                className="flex items-center gap-2 px-4 py-2 bg-[#FF6B00] text-white rounded-lg hover:bg-[#e55d00] transition-colors text-sm"
              >
                <Plus className="w-4 h-4" />
                Ajouter
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Cliquez sur une variable pour l'insérer dans le message
            </p>
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message du modèle <span className="text-red-500">*</span>
            </label>
            <textarea
              id="message-textarea"
              required
              rows={8}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent font-mono text-sm"
              placeholder="Bonjour {prenom},&#10;&#10;Bienvenue sur notre plateforme..."
            />
            <p className="text-sm text-gray-500 mt-1">
              Utilisez {`{nom_variable}`} pour insérer des variables personnalisables
            </p>
          </div>

          {/* Statut */}
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.est_actif}
                onChange={(e) => setFormData({ ...formData, est_actif: e.target.checked })}
                className="w-4 h-4 text-[#FF6B00] focus:ring-[#FF6B00]"
              />
              <span className="font-medium">Modèle actif</span>
            </label>
            <p className="text-sm text-gray-500 ml-7 mt-1">
              Les modèles inactifs ne seront pas visibles dans la liste
            </p>
          </div>
        </form>

        {/* Actions */}
        <div className="border-t p-6 bg-gray-50 sticky bottom-0 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-[#FF6B00] text-white rounded-lg hover:bg-[#e55d00] transition-colors font-medium disabled:opacity-50"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Enregistrer
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateForm;
